import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { DashboardStateService } from '../../../services/dashboard-state.service';
import { KpiViewModel } from '../../../app/components/dashboard/dashboard.component';
import { kpiColor, kpiColorDark } from '../../charts/chart-theme';

export interface KpiTrend {
  direction: 'up' | 'down';
  value: string;
  label?: string;
}

@Component({
  selector: 'app-kpi-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './kpi-card.component.html',
  styleUrl: './kpi-card.component.scss',
})
export class KpiCardComponent implements OnInit, OnDestroy {
  @Input({ required: true }) kpi!: KpiViewModel;
  @Input() colorIndex = 0;
  @Input() compact = false;
  @Input() showComparison = false;
  @Input() dateRangeLabel: string | null = null;
  @Input() showAsTableEnabled = true;
  @Input() actionLabel = 'Show as table';
  @Output() showAsTable = new EventEmitter<KpiViewModel>();

  isActive = false;
  contextMenuPosition: { x: number; y: number } | null = null;
  private activeKey: string | null = null;
  private subscription?: Subscription;

  constructor(private readonly dashboardState: DashboardStateService) {}

  ngOnInit(): void {
    this.subscription = this.dashboardState.activeKpiKey$.subscribe(key => {
      this.activeKey = key;
      this.isActive = !!this.kpi.key && key === this.kpi.key;
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onClick(): void {
    if (this.showAsTableEnabled) {
      this.showAsTable.emit(this.kpi);
      this.closeContextMenu();
      return;
    }

    if (!this.kpi.key) return;
    this.dashboardState.setActiveKpi(this.activeKey === this.kpi.key ? null : this.kpi.key);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onClick();
  }

  onContextMenu(event: MouseEvent): void {
    if (!this.showAsTableEnabled) return;

    event.preventDefault();
    event.stopPropagation();

    const menuWidth = 148;
    const menuHeight = 42;
    const viewportPadding = 8;

    this.contextMenuPosition = {
      x: Math.max(
        viewportPadding,
        Math.min(event.clientX, window.innerWidth - menuWidth - viewportPadding)
      ),
      y: Math.max(
        viewportPadding,
        Math.min(event.clientY, window.innerHeight - menuHeight - viewportPadding)
      ),
    };
  }

  onShowAsTable(event: MouseEvent): void {
    event.stopPropagation();
    this.showAsTable.emit(this.kpi);
    this.closeContextMenu();
  }

  @HostListener('document:click')
  @HostListener('document:contextmenu')
  closeContextMenu(): void {
    this.contextMenuPosition = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeContextMenu();
  }

  get accentColor(): string {
    return kpiColor(this.kpi?.key, this.colorIndex);
  }

  get accentColorDark(): string {
    return kpiColorDark(this.kpi?.key, this.colorIndex);
  }

  get ribbonLabel(): string {
    if (this.kpi.ribbonLabel) return this.kpi.ribbonLabel;
    if (!this.kpi.label) return '';
    const words = this.kpi.label.trim().split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
    return words.map(w => w[0]).join('').slice(0, 4).toUpperCase();
  }

  get trendDisplay(): string | null {
    if (this.kpi.trend?.value) return this.kpi.trend.value;
    if (this.kpi.sourceState !== 'current' || !this.kpi.trendDirection || this.kpi.trendDirection === 'flat') {
      return null;
    }
    return null;
  }

  get trendClass(): string {
    const direction = this.kpi.trend?.direction ?? this.kpi.trendDirection;
    if (direction === 'up') return 'kpi-card__trend--up';
    if (direction === 'down') return 'kpi-card__trend--down';
    return 'kpi-card__trend--flat';
  }

}
